/**
 * ImageCaptureService
 * Manages the capture of images from camera feed into image slots
 * 
 * Requirements: 1.2, 1.3, 1.4, 2.1, 2.3
 */

import type { ImageSlot } from '../types';

export class ImageCaptureService {
  private slots: ImageSlot[];
  private readonly maxSlots: number = 3;

  constructor() {
    this.slots = this.initializeSlots();
  }

  /**
   * Initialize empty slots
   */
  private initializeSlots(): ImageSlot[] {
    return Array.from({ length: this.maxSlots }, (_, index) => ({
      id: `slot-${index}`,
      blob: null,
      dataUrl: null,
      timestamp: null,
    }));
  }

  /**
   * Capture current frame from video element to next available slot
   * Requirements: 1.2, 1.3, 2.1
   * 
   * @param videoElement - The video element to capture from
   * @returns Promise resolving to the filled ImageSlot
   * @throws Error if slots are full or capture fails
   */
  async captureToSlot(videoElement: HTMLVideoElement): Promise<ImageSlot> {
    // Check if slots are full
    if (this.isFull()) {
      throw new Error('All image slots are full. Please remove an image first.');
    }

    // Validate video element
    if (!videoElement || videoElement.readyState < 2) {
      throw new Error('Video not ready for capture. Please wait for camera to initialize.');
    }

    if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
      throw new Error('Video has no dimensions. Please check camera connection.');
    }

    // Find first empty slot
    const emptySlot = this.slots.find(slot => slot.blob === null);
    if (!emptySlot) {
      throw new Error('No empty slot available');
    }

    let canvas: HTMLCanvasElement | null = null;
    let ctx: CanvasRenderingContext2D | null = null;

    try {
      // Create canvas to capture frame
      canvas = document.createElement('canvas');
      
      // Use video dimensions but limit for mobile performance
      const maxDimension = 1920; // Reasonable limit for mobile
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;
      
      let canvasWidth = videoWidth;
      let canvasHeight = videoHeight;
      
      // Scale down if too large (mobile optimization)
      if (videoWidth > maxDimension || videoHeight > maxDimension) {
        const scale = maxDimension / Math.max(videoWidth, videoHeight);
        canvasWidth = Math.floor(videoWidth * scale);
        canvasHeight = Math.floor(videoHeight * scale);
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      ctx = canvas.getContext('2d', {
        alpha: false, // No transparency needed, saves memory
        willReadFrequently: false, // Optimize for single use
      });
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Draw current video frame to canvas
      ctx.drawImage(videoElement, 0, 0, canvasWidth, canvasHeight);

      // Convert canvas to blob with error handling
      const blob = await new Promise<Blob>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Canvas to blob conversion timed out'));
        }, 10000); // 10 second timeout

        canvas!.toBlob(
          (blob) => {
            clearTimeout(timeoutId);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          0.8 // JPEG quality
        );
      });

      // Validate blob
      this.validateImageBlob(blob);

      // Create data URL for thumbnail (smaller quality for memory efficiency)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);

      // Fill the slot
      emptySlot.blob = blob;
      emptySlot.dataUrl = dataUrl;
      emptySlot.timestamp = Date.now();

      console.log(`Image captured to ${emptySlot.id} (${blob.size} bytes)`);
      return emptySlot;
    } catch (error) {
      console.error('Failed to capture image:', error);
      throw error instanceof Error ? error : new Error('Failed to capture image');
    } finally {
      // Clean up canvas to free memory
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;
      }
      ctx = null;
    }
  }

  /**
   * Validate image blob
   * Requirements: 1.4
   * 
   * @param blob - The blob to validate
   * @throws Error if validation fails
   */
  private validateImageBlob(blob: Blob): void {
    // Check if blob exists
    if (!blob) {
      throw new Error('Invalid image: blob is null');
    }

    // Check blob type
    if (!blob.type.startsWith('image/')) {
      throw new Error(`Invalid image format: ${blob.type}. Only image formats are allowed.`);
    }

    // Check blob size (max 10MB as per backend)
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    if (blob.size > maxSizeBytes) {
      throw new Error(`Image too large: ${(blob.size / 1024 / 1024).toFixed(2)}MB. Maximum size is 10MB.`);
    }

    // Check minimum size
    if (blob.size === 0) {
      throw new Error('Invalid image: file is empty');
    }
  }

  /**
   * Get all slots (including empty ones)
   * 
   * @returns Array of all image slots
   */
  getSlots(): ImageSlot[] {
    return [...this.slots];
  }

  /**
   * Get only filled slots
   * Requirements: 1.2
   * 
   * @returns Array of filled image slots
   */
  getFilledSlots(): ImageSlot[] {
    return this.slots.filter(slot => slot.blob !== null);
  }

  /**
   * Remove image from specific slot
   * Requirements: 2.3
   * 
   * @param slotId - The ID of the slot to clear
   */
  clearSlot(slotId: string): void {
    const slot = this.slots.find(s => s.id === slotId);
    if (!slot) {
      console.warn(`Slot ${slotId} not found`);
      return;
    }

    // Revoke object URL if it exists to free memory
    if (slot.dataUrl && slot.dataUrl.startsWith('blob:')) {
      URL.revokeObjectURL(slot.dataUrl);
    }

    // Clear slot
    slot.blob = null;
    slot.dataUrl = null;
    slot.timestamp = null;

    console.log(`Cleared slot ${slotId}`);
  }

  /**
   * Clear all slots
   * Requirements: 2.3
   */
  clearAllSlots(): void {
    this.slots.forEach(slot => {
      if (slot.dataUrl && slot.dataUrl.startsWith('blob:')) {
        URL.revokeObjectURL(slot.dataUrl);
      }
      slot.blob = null;
      slot.dataUrl = null;
      slot.timestamp = null;
    });

    console.log('Cleared all slots');
  }

  /**
   * Check if all slots are full
   * Requirements: 1.3, 1.4
   * 
   * @returns true if all slots are filled
   */
  isFull(): boolean {
    return this.slots.every(slot => slot.blob !== null);
  }

  /**
   * Check if any slots are filled
   * Requirements: 2.4
   * 
   * @returns true if at least one slot has an image
   */
  hasImages(): boolean {
    return this.slots.some(slot => slot.blob !== null);
  }

  /**
   * Get number of filled slots
   * Requirements: 1.2
   * 
   * @returns Count of filled slots
   */
  getFilledCount(): number {
    return this.slots.filter(slot => slot.blob !== null).length;
  }

  /**
   * Get maximum number of slots
   * 
   * @returns Maximum slot count
   */
  getMaxSlots(): number {
    return this.maxSlots;
  }

  /**
   * Get specific slot by ID
   * 
   * @param slotId - The ID of the slot to retrieve
   * @returns The slot or undefined if not found
   */
  getSlot(slotId: string): ImageSlot | undefined {
    return this.slots.find(s => s.id === slotId);
  }
}

export default ImageCaptureService;
